// AlertToast.jsx
// Single-alert display unit. Slides in from the side, holds, slides out, fires
// onComplete. Position (top-right / bottom-right) is determined by the alert
// type's default in ALERT_DEFAULTS, with optional per-alert override.
//
// Total lifecycle: ~4 seconds (slide-in 0.6s, hold 2.8s, slide-out 0.6s).
import { useEffect, useRef, useState } from "react";
import useOrientation from "./../../hooks/useOrientation";
import CharacterBadge from "./CharacterBadge";
import { playAlertSound } from "./../../utils/alertSounds";

const HOLD_MS  = 2800;
const ENTER_MS = 600;
const EXIT_MS  = 600;

// LANDSCAPE: top positions clear the main overlay's 96px top bar with
// breathing room. Right-side positions are kept for completeness but the
// default alert defs avoid them — Main's queue card column
// lives on the right side and we don't want alerts covering them.
const POSITIONS = {
  "top-left":     { top: "120px", bottom: "auto", left:  "3%" },
  "top-right":    { top: "120px", bottom: "auto", right: "3%" },
  "bottom-left":  { top: "auto",  bottom: "8%",   left:  "3%" },
  "bottom-right": { top: "auto",  bottom: "8%",   right: "3%" },
};

// PORTRAIT (1080×1920 rig): every alert lands in ONE canonical spot — the
// top-right corner of the live camera window — regardless of the alert
// type's landscape position. Booth's feed window starts below the 108px top
// bar + max(150px, 11vh) status rail (2026-07-07 restack — the flash band
// moved to the shared bottom FlashBrandBand; mirror Booth.jsx if that stack
// changes) and toasts must not pop over the rail's cards; anchoring inside
// the live view keeps them off every band on Booth, clears the IG host bar
// (~250px), and lands over the reel/cam video on Break. Main (calm) uses
// Booth's exact top bar + rail, so the spot is identical there.
const PORTRAIT_POS = {
  top: "calc(108px + max(150px, 11vh) + 20px)",
  bottom: "auto",
  right: "24px",
};

export default function AlertToast({ alert, message, onComplete }) {
  const orientation = useOrientation();
  const portrait = orientation === "portrait";
  const [phase, setPhase] = useState("entering"); // entering | holding | exiting
  const completeRef = useRef(onComplete);
  completeRef.current = onComplete;

  useEffect(() => {
    // Fire the synthesized sound on entrance. playAlertSound is a no-op if
    // muted or if the audio context is suspended (browser autoplay block).
    playAlertSound(alert?.type);

    const t1 = setTimeout(() => setPhase("holding"), ENTER_MS);
    const t2 = setTimeout(() => setPhase("exiting"), ENTER_MS + HOLD_MS);
    const t3 = setTimeout(() => completeRef.current && completeRef.current(), ENTER_MS + HOLD_MS + EXIT_MS);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [alert?.id, alert?.type]); // re-run lifecycle if same component is reused for a new alert

  const pos = portrait ? PORTRAIT_POS : (POSITIONS[alert.position] || POSITIONS["top-right"]);
  // Portrait always slides from the right (it's pinned right).
  const fromLeft = !portrait && alert.position?.endsWith("-left");
  const enterFrom = fromLeft ? "translateX(-120%)" : "translateX(120%)";
  const exitTo    = fromLeft ? "translateX(-120%)" : "translateX(120%)";

  let transform = "translateX(0)";
  let opacity = 1;
  if (phase === "entering") { transform = enterFrom; opacity = 0; }
  if (phase === "exiting")  { transform = exitTo;    opacity = 0; }

  // Bounce keyframe — runs once on entrance after the slide settles
  const characterAnim = phase === "holding"
    ? "jopAlertCharBounce 0.6s ease-out"
    : "none";

  return (
    <div
      style={{
        position: "absolute",
        ...pos,
        maxWidth: "520px",
        minWidth: "340px",
        display: "flex",
        alignItems: "center",
        gap: "16px",
        padding: "16px 22px",
        background: alert.color || "var(--jop-blue)",
        color: "white",
        borderRadius: "18px",
        border: "3px solid rgba(255,255,255,0.6)",
        boxShadow: "0 12px 40px rgba(0,0,0,0.55), 0 0 0 4px rgba(0,0,0,0.25)",
        transform,
        opacity,
        transition: `transform ${phase === "entering" ? ENTER_MS : EXIT_MS}ms cubic-bezier(0.34,1.56,0.64,1), opacity ${phase === "entering" ? ENTER_MS : EXIT_MS}ms ease`,
        overflow: "hidden",
        pointerEvents: "none",
      }}
    >
      {/* Shimmer sweep over the entire toast during hold */}
      {phase === "holding" && (
        <div style={{
          position: "absolute", inset: 0, pointerEvents: "none",
          background: "linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.35) 50%, transparent 60%)",
          animation: "jopCardShimmer 1.2s 0.3s ease-out forwards",
          transform: "translateX(-100%)",
        }} />
      )}

      <div style={{ animation: characterAnim, transformOrigin: "center bottom" }}>
        <CharacterBadge characterKey={alert.character} size={84} />
      </div>

      <div style={{
        flex: 1,
        fontSize: "1.5rem",
        fontWeight: 800,
        lineHeight: 1.25,
        textShadow: "0 2px 8px rgba(0,0,0,0.45)",
        letterSpacing: "0.01em",
      }}>
        {message}
      </div>
    </div>
  );
}
